import { IdentityService } from './../identity/identity.service';
import { Injectable, inject } from '@angular/core';
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot, NavigationExtras } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, timeout } from 'rxjs/operators';
import { LoggerFactory, Logger } from './../../core/logger.service';
import { SiteContextService } from '../site-context.service';
import { CurrentContextService } from '../current-context.service';

/** A failed load must not leave the route hanging. */
const CONTEXT_TIMEOUT_MS = 10000;

@Injectable()
export class VerifiedGuard {
  private readonly logger: Logger;
  private readonly currentContextService = inject(CurrentContextService);
  constructor(
    protected readonly identityService: IdentityService,
    protected readonly router: Router,
    private readonly siteContext: SiteContextService,
    loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.get('VerifiedGuard');
  }
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (!this.identityService.isUserVerified) {
      this.redirectToSignUp(state.url);
      return false;
    }

    return this.swichAccountByQuerystring(route, state);
  }

  private redirectToSignUp(currentUrl?: string) {
    const url = '/account/login';
    const queryParams: NavigationExtras = currentUrl ? { queryParams: { callbackUrl: currentUrl } } : undefined;
    this.logger.debug('Redirecting to sign up. Current url is ' + currentUrl);
    this.logger.info(
      'The user is not verified and will be redirected to "' +
        url +
        '" with extra settings' +
        JSON.stringify(queryParams),
    );
    this.router.navigate([url], queryParams);
  }

  private swichAccountByQuerystring(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Promise<boolean> {
    const newSiteId: number = +route.queryParams['siteId'];
    if (!newSiteId) {
      return true;
    }

    if (newSiteId === this.siteContext.getCurrentSiteId()) {
      return true;
    }

    if (this.identityService.currentIdentity.siteIds.indexOf(newSiteId) === -1) {
      this.logger.error(`Site does not exist in allowed sites, therefore ${newSiteId} cannot be set as current site.`);
      return true;
    }

    return this.switchSiteAndRetry(newSiteId, route, state);
  }

  private async switchSiteAndRetry(
    newSiteId: number,
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    try {
      await this.siteContext.setCurrentSiteAsync(newSiteId, false);
      await this.waitForContext(newSiteId);
    } catch (error) {
      this.logger.error(`Could not switch to site ${newSiteId}. Error: '${JSON.stringify(error)}'`);
      return true;
    }

    const url = state.url.split('?')[0];
    const queryParams = { ...route.queryParams };
    delete queryParams['siteId'];
    this.router.navigate([url], { queryParams: queryParams });

    return false;
  }

  /** Pages read their site from the context, which the switch does not rebuild: activating before
   *  it catches up sends the first requests to the previous site. */
  private async waitForContext(siteId: number): Promise<void> {
    this.currentContextService.loadCurrentContext(true);
    await firstValueFrom(
      this.currentContextService.currentContextBS.pipe(
        filter((context) => context?.account?.siteId === siteId),
        timeout(CONTEXT_TIMEOUT_MS),
        catchError(() => of(undefined)),
      ),
    );
  }
}
